import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Box, Paper, Typography, Divider, Button, Chip, Skeleton } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ListCard from "../../components/ui/cards/ListCard";
import { InlineListSkeleton } from "./_PortalSkeletons";
import { useActAsClient } from "./ActAsClientContext";

// ✅ Main app auth (staff session) + portal auth (client session)
import { auth } from "../../firebase-config";
import { authPortal } from "../../firebase-portal";

const GET_ASSIGNED_DOCS_URL =
  process.env.REACT_APP_GET_ASSIGNED_DOCS_URL ||
  "https://getclientassigneddocuments-774073159618.us-central1.run.app";
const GET_ASSIGNED_FORMS_URL =
  process.env.REACT_APP_GET_ASSIGNED_FORMS_URL ||
  "https://getclientforms-774073159618.us-central1.run.app";
const GET_COMPLETED_FORMS_URL =
  process.env.REACT_APP_GET_COMPLETED_FORMS_URL ||
  "https://getclientcompletedforms-774073159618.us-central1.run.app";

const PREVIEW_COUNT = 5;

export default function ClientPortalDashboard() {
  const [docs, setDocs] = useState([]);
  const [todoForms, setTodoForms] = useState([]);
  const [doneForms, setDoneForms] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  // --- Act-as context (staff impersonation) ---
  const actAs = useActAsClient();
  const [params] = useSearchParams();

  const tsToLocale = (ts) =>
    ts?.toDate
      ? ts.toDate().toLocaleString()
      : ts?._seconds
      ? new Date(ts._seconds * 1000).toLocaleString()
      : ts
      ? new Date(ts).toLocaleString()
      : "Unknown";

  useEffect(() => {
    // If URL indicates act-as but context not populated yet, wait.
    const isExpectingActAs = params.has("actAsClient") || params.has("as");
    if (isExpectingActAs && !actAs.isActingAs) return;

    const run = async () => {
      setLoading(true);
      try {
        const isActingAs = !!actAs?.isActingAs;

        // ✅ Choose the correct auth source:
        // - Acting-as: use staff (main app) auth token
        // - Normal client: use portal auth token (magic-link session)
        const currentUser = isActingAs ? auth.currentUser : authPortal.currentUser;

        if (!currentUser) {
          throw new Error(isActingAs ? "No staff user" : "No portal user");
        }

        const idToken = await currentUser.getIdToken(true);

        const actAsHeaders = isActingAs ? actAs.getActAsHeaders() : {};
        const headers = { Authorization: `Bearer ${idToken}`, ...actAsHeaders };

        const [dRes, fRes, cRes] = await Promise.all([
          axios.post(GET_ASSIGNED_DOCS_URL, {}, { headers }),
          axios.post(GET_ASSIGNED_FORMS_URL, {}, { headers }),
          axios.post(GET_COMPLETED_FORMS_URL, {}, { headers }),
        ]);

        setDocs(Array.isArray(dRes?.data?.documents) ? dRes.data.documents : []);
        setTodoForms(Array.isArray(fRes?.data?.forms) ? fRes.data.forms : []);
        setDoneForms(Array.isArray(cRes?.data?.forms) ? cRes.data.forms : []);
      } catch (e) {
        console.error("Portal dashboard load failed:", e);
        setDocs([]);
        setTodoForms([]);
        setDoneForms([]);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [actAs, params]);

  const docsPreview = useMemo(() => (docs || []).slice(0, PREVIEW_COUNT), [docs]);
  const todoPreview = useMemo(() => (todoForms || []).slice(0, PREVIEW_COUNT), [todoForms]);
  const donePreview = useMemo(() => (doneForms || []).slice(0, PREVIEW_COUNT), [doneForms]);

  return (
    <Box>
      {/* Staff "Acting as" banner */}
      {actAs?.isActingAs && (
        <Box
          sx={{
            mb: 2,
            p: 1.25,
            borderRadius: 1,
            bgcolor: "#FFFBEB",
            border: "1px solid #FCD34D",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#92400E" }}>
            Staff preview: acting as{" "}
            <span style={{ textDecoration: "underline" }}>
              {actAs.actAsName || actAs.clientId}
            </span>
            . All actions are recorded.
          </Typography>
        </Box>
      )}

      <Typography variant="h5" sx={{ mb: 2 }}>
        Welcome To Your Client Portal
      </Typography>

      {/* Assigned Documents (preview) */}
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1, mb: 3 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Typography variant="h6" sx={{ mb: 1 }}>
              Assigned Documents
            </Typography>
            {loading ? (
              <Skeleton variant="rounded" width={42} height={24} />
            ) : (
              <Chip size="small" label={`${docs?.length || 0}`} color="primary" variant="outlined" />
            )}
          </div>
          <Button size="small" variant="text" onClick={() => navigate("/portal/documents")}>
            View All
          </Button>
        </div>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <InlineListSkeleton rows={PREVIEW_COUNT} />
        ) : (
          <ListCard
            inset
            items={docsPreview}
            emptyMessage="No documents assigned."
            renderItem={(doc) => {
              const link = doc?.url || doc?.downloadUrl;

              const rawDue = doc?.dueDate;
              const dueMs =
                rawDue && typeof rawDue === "object" && "_seconds" in rawDue
                  ? rawDue._seconds * 1000
                  : rawDue
                  ? new Date(rawDue).getTime()
                  : null;
              const hasValidDue = Number.isFinite(dueMs);
              const overdue = hasValidDue ? dueMs < Date.now() : false;

              const chipStyles = overdue
                ? { backgroundColor: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" }
                : { backgroundColor: "#E0E7FF", color: "#1E3A8A", border: "1px solid #C7D2FE" };

              return (
                <div className="flex justify-between items-center w-full">
                  <div className="flex-1">
                    <div className="font-semibold">{doc?.name || "Document"}</div>
                    <div className="text-xs text-gray-500">
                      Assigned: {tsToLocale(doc?.assignedAt) || "Unknown"}
                    </div>
                    {hasValidDue && (
                      <span
                        title={new Date(dueMs).toLocaleString()}
                        style={{
                          display: "inline-block",
                          marginTop: 6,
                          borderRadius: 12,
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 600,
                          ...chipStyles,
                        }}
                      >
                        {overdue ? "Overdue" : "Due"} {new Date(dueMs).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!link}
                    onClick={() => link && window.open(link, "_blank")}
                  >
                    View
                  </Button>
                </div>
              );
            }}
          />
        )}
      </Paper>

      {/* Forms To Do (preview) */}
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1, mb: 3 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Typography variant="h6" sx={{ mb: 1 }}>
              Forms To Do
            </Typography>
            {loading ? (
              <Skeleton variant="rounded" width={42} height={24} />
            ) : (
              <Chip size="small" label={`${todoForms?.length || 0}`} color="primary" variant="outlined" />
            )}
          </div>
          <Button size="small" variant="text" onClick={() => navigate("/portal/forms")}>
            View All
          </Button>
        </div>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <InlineListSkeleton rows={PREVIEW_COUNT} />
        ) : (
          <ListCard
            inset
            items={todoPreview}
            emptyMessage="No forms assigned."
            renderItem={(form) => {
              const rawDue = form?.dueDate;
              const dueMs =
                rawDue && typeof rawDue === "object" && "_seconds" in rawDue
                  ? rawDue._seconds * 1000
                  : rawDue
                  ? new Date(rawDue).getTime()
                  : null;

              const hasValidDue = Number.isFinite(dueMs);
              const overdue = hasValidDue ? dueMs < Date.now() : false;

              const chipStyles = overdue
                ? { backgroundColor: "#FEE2E2", color: "#991B1B", border: "1px solid #FCA5A5" }
                : { backgroundColor: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D" };

              return (
                <div className="flex justify-between items-center w-full">
                  <div className="flex-1">
                    <div className="font-semibold">{form?.title || form?.name || "Untitled Form"}</div>
                    <div className="text-xs text-gray-500">Last updated: {tsToLocale(form?.updatedAt)}</div>
                    {hasValidDue && (
                      <span
                        title={new Date(dueMs).toLocaleString()}
                        style={{
                          display: "inline-block",
                          marginTop: 6,
                          borderRadius: 12,
                          padding: "2px 8px",
                          fontSize: 12,
                          fontWeight: 600,
                          ...chipStyles,
                        }}
                      >
                        {overdue ? "Overdue" : "Due"} {new Date(dueMs).toLocaleDateString()}
                      </span>
                    )}
                  </div>

                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => navigate(`/portal/forms/fill/${form.id}`, { state: { clientMode: true } })}
                  >
                    Fill
                  </Button>
                </div>
              );
            }}
          />
        )}
      </Paper>

      {/* Completed Forms (preview) */}
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 1 }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Typography variant="h6" sx={{ mb: 1 }}>
              Completed Forms
            </Typography>
            {loading ? (
              <Skeleton variant="rounded" width={42} height={24} />
            ) : (
              <Chip size="small" label={`${doneForms?.length || 0}`} color="primary" variant="outlined" />
            )}
          </div>
          <Button size="small" variant="text" onClick={() => navigate("/portal/forms/completed")}>
            View All
          </Button>
        </div>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <InlineListSkeleton rows={PREVIEW_COUNT} />
        ) : (
          <ListCard
            inset
            items={donePreview}
            emptyMessage="No completed forms."
            renderItem={(f) => (
              <div className="flex justify-between items-center w-full">
                <div className="flex-1">
                  <div className="font-semibold">{f?.title || f?.name || "Form"}</div>
                  <div className="text-xs text-gray-500">Submitted: {tsToLocale(f?.submittedAt)}</div>
                </div>
                {f?.formPdfUrl && (
                  <Button size="small" variant="outlined" onClick={() => window.open(f.formPdfUrl, "_blank")}>
                    View PDF
                  </Button>
                )}
              </div>
            )}
          />
        )}
      </Paper>
    </Box>
  );
}